import { getDefaultRouteAfterLogin, isExplorerOnlyBlockedRoute } from "@/constants";
import { useCookieStore } from "@/store/cookie";
import { useAuthActions, useAuthState } from "@/store/auth";
import { useNotificationStore } from "@/store/notification";
import { useUserStore } from "@/store/user";
import { CookiePreferenceType } from "@/types/user";
import {
  initialiseAmplitude,
  syncFirstTouchAttribution,
  updateAmplitudeUserOptions
} from "@/utils/amplitude";
import { redirectOnce, redirectUsingQuery } from "@/utils/location";
import { sanitizeRedirectUrl } from "@/utils/sanitize";
import { getToolUrl } from "@/utils/tool";
import React, { useEffect, useState } from "react";
import { useLocation } from "wouter";
import localCookies from "@/utils/cookie";
import { ClientAnalyticsTracker } from "../Common/ClientAnalyticsTracker/ClientAnalyticsTracker";
import { trackEvent } from "@/utils/analytics";
import { AnalyticsEventType } from "@/constants/analytics";

export const AuthProcess: React.FC = () => {
  const { bootstrap } = useAuthActions();
  const status = useAuthState((state) => state.status);
  const isReady = useAuthState((state) => state.isReady);
  const profile = useAuthState((state) => state.profile);
  const authUserId = useAuthState((state) => state.authUserId);
  const isNewUser = useAuthState((state) => state.isNewUser);
  const ssoType = useAuthState((state) => state.ssoType) || "email";
  const displayError = useNotificationStore((state) => state.displayError);
  const [location, setLocation] = useLocation();
  const [hasRedirected, setHasRedirected] = useState(false);
  const isLoggedIn = status === "authenticated";

  useEffect(() => {
    bootstrap().catch((e) => {
      displayError({
        title: "ERROR",
        message: (e as Error).message || "Failed to bootstrap auth",
        isPublic: true
      });
    });
  }, [bootstrap, displayError]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    const cookiePreferences = useCookieStore.getState().cookiePreferences;
    if (cookiePreferences.includes(CookiePreferenceType.ANALYTICS)) {
      initialiseAmplitude({
        userId: isLoggedIn ? useUserStore.getState().userId : undefined,
        defaultTracking: {
          attribution: false
        }
      });
    }

    if (isLoggedIn) {
      const cachedUserInfo = useUserStore.getState().userInfo;
      if (cachedUserInfo) {
        syncFirstTouchAttribution();
        updateAmplitudeUserOptions(cachedUserInfo);
        return;
      }

      void (async () => {
        const userInfoResponse = await useUserStore.getState().fetchUserInfo();
        syncFirstTouchAttribution();
        updateAmplitudeUserOptions(userInfoResponse);
      })();
    }
  }, [isReady, isLoggedIn]);

  useEffect(() => {
    if (!isReady || !isLoggedIn) {
      return;
    }
    if (hasRedirected || !profile || !authUserId || localCookies.getItem("LOGIN_TRACKED")) {
      return;
    }

    const run = () => {
      try {
        const rawRoute = localCookies.getItem("LOGIN_REDIRECT_ROUTE");
        const sanitized = rawRoute ? sanitizeRedirectUrl(rawRoute) : "";
        const rawRedirectRoute = sanitized && !sanitized.startsWith("/login") ? sanitized : "";
        const redirectRoute = rawRedirectRoute && isExplorerOnlyBlockedRoute(rawRedirectRoute)
          ? getDefaultRouteAfterLogin()
          : rawRedirectRoute;

        setHasRedirected(true);
        localCookies.setItem("LOGIN_TRACKED", "true");

        if (isNewUser) {
          const screen = redirectRoute.split("/")[1];
          const eventProps = {
            type: ssoType,
            screen: screen === "login" ? "id" : screen
          };
          trackEvent(AnalyticsEventType.USER_CREATED, eventProps);
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: AnalyticsEventType.USER_CREATED,
            ...eventProps
          });
        }

        localCookies.removeItem("LOGIN_REDIRECT_ROUTE");

        if (redirectRoute) {
          const prefix = redirectRoute.split("/")[1];
          const isValid = window.location.hostname.startsWith(prefix) || prefix === "login";

          if (isValid) {
            setLocation(redirectRoute);
          } else {
            const newUrl = getToolUrl(prefix, redirectRoute);
            redirectOnce(newUrl);
          }
          return;
        }

        if (location.indexOf("/login") > -1) {
          setLocation(redirectUsingQuery());
        }
      } catch (e) {
        console.error(e);
      }
    };

    run();
  }, [
    authUserId,
    hasRedirected,
    isLoggedIn,
    isNewUser,
    isReady,
    location,
    profile,
    ssoType,
    setLocation
  ]);

  return <ClientAnalyticsTracker isInitialised={isReady} />;
};
